import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState, type DragEvent } from "react";
import {
  FiChevronDown,
  FiChevronRight,
  FiRepeat,
  FiSearch,
  FiUserPlus,
} from "react-icons/fi";

import { isApiClientError } from "../api/errors";
import { hasPermission } from "../entities/access/permissions";
import type { HierarchyNode } from "../entities/access";
import { shellBootstrapQuery } from "./bootstrap";
import {
  ancestorIds,
  branchIds,
  buildHierarchy,
  searchVisibility,
  selfAndDescendantIds,
  type Hierarchy,
  type TreeNode,
} from "./hierarchyTree";
import type { ShellServices } from "./services";
import { EmptyState, LoadingPage } from "./states";
import { Modal } from "./UserManagementPage";
import { Select } from "./Select";

const hierarchyKey = ["hierarchy", "tree"] as const;

/** С этого уровня на телефоне отступ перестаёт расти, а карточка получает метку уровня. */
const DEEP_LEVEL = 3;

type Notice = { tone: "success" | "error"; text: string } | null;
type Move = { userId: string; managerId: string | null };
type Dialog = { kind: "manager" | "reports"; id: string } | null;

const statusLabels: Partial<Record<HierarchyNode["status"], string>> = {
  business_trip: "В командировке",
  vacation: "В отпуске",
  sick_leave: "На больничном",
  blocked: "Заблокирован",
  disabled: "Отключён",
  invited: "Приглашён",
  pending_activation: "Не активирован",
  terminated: "Уволен",
  archived: "В архиве",
};

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function describe(node: HierarchyNode): string {
  return node.position
    ? `${node.display_name} — ${node.position}`
    : node.display_name;
}

function moveError(error: unknown): string {
  if (isApiClientError(error)) {
    if (error.status === 403)
      return "Недостаточно прав для изменения иерархии.";
    if (error.status === 422) {
      const details = error.details as { fields?: Record<string, string[]> };
      const message = Object.values(details.fields ?? {}).flat()[0] ?? "";
      if (/subordinate of the employee/i.test(message)) {
        return "Так нельзя: руководитель не может быть подчинённым своего подчинённого.";
      }
      if (/themselves/i.test(message)) {
        return "Сотрудник не может подчиняться сам себе.";
      }
    }
  }
  return "Не удалось изменить иерархию. Повторите попытку.";
}

function pluralize(count: number, forms: [string, string, string]): string {
  const mod100 = count % 100;
  const mod10 = count % 10;
  if (mod100 >= 11 && mod100 <= 14) return forms[2];
  if (mod10 === 1) return forms[0];
  if (mod10 >= 2 && mod10 <= 4) return forms[1];
  return forms[2];
}

/** Что делает интерфейс с деревом: собрано в один объект, чтобы не тащить пропсы по ветвям. */
interface BranchUi {
  canManage: boolean;
  collapsed: ReadonlySet<string>;
  matched: ReadonlySet<string> | null;
  visible: ReadonlySet<string> | null;
  dragId: string | null;
  dragBlocked: ReadonlySet<string>;
  overId: string | null;
  toggle: (id: string) => void;
  openDialog: (dialog: NonNullable<Dialog>) => void;
  startDrag: (id: string) => void;
  endDrag: () => void;
  setOver: (id: string | null) => void;
  drop: (targetId: string) => void;
}

export function UserHierarchyPage({ services }: { services: ShellServices }) {
  const client = useQueryClient();
  const bootstrap = useQuery(shellBootstrapQuery(services));
  const canManage =
    bootstrap.data !== undefined &&
    hasPermission(
      { permissions: bootstrap.data.permissions },
      "users.hierarchy.manage",
    );
  const nodes = useQuery({
    queryKey: hierarchyKey,
    queryFn: () => services.administration.hierarchy(),
  });
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [notice, setNotice] = useState<Notice>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const hierarchy = useMemo(
    () => buildHierarchy(nodes.data ?? []),
    [nodes.data],
  );
  const filter = useMemo(
    () => searchVisibility(hierarchy, search),
    [hierarchy, search],
  );
  const dragBlocked = useMemo(() => {
    const tree = dragId === null ? undefined : hierarchy.byId.get(dragId);
    return tree === undefined ? new Set<string>() : selfAndDescendantIds(tree);
  }, [dragId, hierarchy]);

  const relocate = useMutation({
    mutationFn: async (moves: Move[]) => {
      for (const move of moves) {
        await services.administration.setManager(move.userId, move.managerId);
      }
    },
    onMutate: async (moves) => {
      await client.cancelQueries({ queryKey: hierarchyKey });
      const previous = client.getQueryData<HierarchyNode[]>(hierarchyKey);
      const targets = new Map(
        moves.map((move) => [move.userId, move.managerId]),
      );
      client.setQueryData<HierarchyNode[]>(hierarchyKey, (current) =>
        current?.map((node) =>
          targets.has(node.id)
            ? { ...node, manager_id: targets.get(node.id) ?? null }
            : node,
        ),
      );
      return { previous };
    },
    onError: (error, _moves, context) => {
      client.setQueryData(hierarchyKey, context?.previous);
      setNotice({ tone: "error", text: moveError(error) });
    },
    onSettled: () => {
      // Круг подчинённых влияет на списки выбора в карточках документов.
      void client.invalidateQueries({ queryKey: ["hierarchy"] });
    },
  });

  const run = (moves: Move[], success: string) => {
    setNotice(null);
    relocate.mutate(moves, {
      onSuccess: () => setNotice({ tone: "success", text: success }),
    });
  };

  const byName = (id: string) =>
    hierarchy.byId.get(id)?.node.display_name ?? "";

  const drop = (targetId: string) => {
    const id = dragId;
    setDragId(null);
    setOverId(null);
    if (id === null || dragBlocked.has(targetId)) return;
    const moved = hierarchy.byId.get(id)?.node;
    if (moved === undefined || moved.manager_id === targetId) return;
    run(
      [{ userId: id, managerId: targetId }],
      `«${moved.display_name}» теперь подчиняется «${byName(targetId)}».`,
    );
  };

  const ui: BranchUi = {
    canManage,
    collapsed,
    matched: filter?.matched ?? null,
    visible: filter?.visible ?? null,
    dragId,
    dragBlocked,
    overId,
    toggle: (id) =>
      setCollapsed((current) => {
        const next = new Set(current);
        if (!next.delete(id)) next.add(id);
        return next;
      }),
    openDialog: setDialog,
    startDrag: setDragId,
    endDrag: () => {
      setDragId(null);
      setOverId(null);
    },
    setOver: setOverId,
    drop,
  };

  if (nodes.isPending || bootstrap.isPending) {
    return <LoadingPage label="Загружаем иерархию сотрудников" />;
  }
  if (nodes.isError) {
    return (
      <section className="workspace-page" aria-labelledby="hierarchy-title">
        <div className="page-heading">
          <p className="eyebrow">Пользователи</p>
          <h1 id="hierarchy-title">Иерархия</h1>
        </div>
        <EmptyState
          title="Не удалось загрузить иерархию"
          description="Проверьте подключение и повторите попытку."
          action={{ label: "Повторить", onClick: () => void nodes.refetch() }}
        />
      </section>
    );
  }

  const total = nodes.data.length;
  const placed = total - hierarchy.unplaced.length;
  const dialogTree =
    dialog === null ? undefined : hierarchy.byId.get(dialog.id);
  const dragging = dragId !== null;
  const draggedHasManager =
    dragId !== null && hierarchy.byId.get(dragId)?.node.manager_id != null;
  const visibleRoots =
    filter === null
      ? hierarchy.roots
      : hierarchy.roots.filter((tree) => filter.visible.has(tree.node.id));
  const visibleUnplaced =
    filter === null
      ? hierarchy.unplaced
      : hierarchy.unplaced.filter((node) => filter.matched.has(node.id));

  return (
    <section
      className="workspace-page hierarchy-page"
      aria-labelledby="hierarchy-title"
    >
      <div className="page-heading">
        <p className="eyebrow">Пользователи</p>
        <h1 id="hierarchy-title">Иерархия</h1>
        <p>
          Линейная структура иерархии. Руководитель может выдавать доступ к
          документам и передавать их на исполнение только своим подчинённым —
          прямым и стоящим ниже по цепочке.
        </p>
      </div>

      <ul className="hierarchy-stats" aria-label="Сводка по иерархии">
        <li>
          <strong>{total}</strong>
          <span>
            {pluralize(total, ["сотрудник", "сотрудника", "сотрудников"])}
          </span>
        </li>
        <li>
          <strong>{placed}</strong>
          <span>в структуре</span>
        </li>
        <li>
          <strong>{hierarchy.unplaced.length}</strong>
          <span>не размещены</span>
        </li>
        <li>
          <strong>{hierarchy.depth}</strong>
          <span>
            {pluralize(hierarchy.depth, ["уровень", "уровня", "уровней"])}
          </span>
        </li>
      </ul>

      {notice ? (
        <div
          className={`operation-notice operation-notice--${notice.tone}`}
          role="status"
        >
          {notice.text}
        </div>
      ) : null}

      <div className="hierarchy-toolbar">
        <label className="hierarchy-search">
          <FiSearch aria-hidden="true" />
          <input
            type="search"
            value={search}
            placeholder="Имя, должность или почта"
            aria-label="Поиск по иерархии"
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <div className="inline-actions">
          <button
            className="button button--secondary button--compact"
            type="button"
            onClick={() => setCollapsed(new Set())}
          >
            Развернуть всё
          </button>
          <button
            className="button button--secondary button--compact"
            type="button"
            onClick={() => setCollapsed(new Set(branchIds(hierarchy)))}
          >
            Свернуть всё
          </button>
        </div>
      </div>
      {canManage ? (
        <p className="hierarchy-hint">
          Перетащите сотрудника на его нового руководителя или воспользуйтесь
          кнопками на карточке.
        </p>
      ) : null}

      {total === 0 ? (
        <EmptyState
          title="Сотрудников пока нет"
          description="Заведите пользователей — затем их можно выстроить в иерархию."
        />
      ) : (
        <div className="hierarchy-layout">
          <div className="hierarchy-main">
            {hierarchy.roots.length === 0 ? (
              <EmptyState
                title="Иерархия ещё не построена"
                description={
                  canManage
                    ? "Выберите директора в списке справа и добавьте ему подчинённых — дерево вырастет вниз по цепочке."
                    : "Иерархию ещё не настроили: обратитесь к администратору."
                }
              />
            ) : visibleRoots.length === 0 ? (
              <p className="muted">Никого не нашли по запросу.</p>
            ) : (
              <ul className="hierarchy-tree" aria-label="Дерево сотрудников">
                {visibleRoots.map((tree) => (
                  <HierarchyBranch
                    key={tree.node.id}
                    tree={tree}
                    ui={ui}
                    depth={0}
                  />
                ))}
              </ul>
            )}
            {canManage && dragging && draggedHasManager ? (
              <div
                className={`hierarchy-detach${overId === "__detach" ? " hierarchy-detach--over" : ""}`}
                onDragOver={(event) => {
                  event.preventDefault();
                  setOverId("__detach");
                }}
                onDragLeave={() => setOverId(null)}
                onDrop={(event) => {
                  event.preventDefault();
                  const id = dragId;
                  setDragId(null);
                  setOverId(null);
                  const moved =
                    id === null ? undefined : hierarchy.byId.get(id)?.node;
                  if (moved !== undefined) {
                    run(
                      [{ userId: moved.id, managerId: null }],
                      `«${moved.display_name}» больше никому не подчиняется.`,
                    );
                  }
                }}
              >
                Отпустите здесь, чтобы снять руководителя
              </div>
            ) : null}
          </div>

          <aside
            className="hierarchy-side"
            aria-labelledby="hierarchy-unplaced"
          >
            <h2 id="hierarchy-unplaced">Не размещены</h2>
            <p className="muted">
              Без руководителя и без подчинённых. Сделайте руководителем того,
              кто возглавит структуру, и добавьте ему людей.
            </p>
            {visibleUnplaced.length === 0 ? (
              <p className="muted">
                {filter === null
                  ? "Все сотрудники размещены."
                  : "Никого не нашли."}
              </p>
            ) : (
              <ul className="hierarchy-unplaced">
                {visibleUnplaced.map((node) => (
                  <li key={node.id}>
                    <PersonCard
                      tree={{ node, children: [], descendants: 0 }}
                      ui={ui}
                      expandable={false}
                    />
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </div>
      )}

      {dialog?.kind === "manager" && dialogTree !== undefined ? (
        <ManagerDialog
          tree={dialogTree}
          hierarchy={hierarchy}
          pending={relocate.isPending}
          onClose={() => setDialog(null)}
          onSubmit={(managerId) => {
            setDialog(null);
            if (managerId === dialogTree.node.manager_id) return;
            run(
              [{ userId: dialogTree.node.id, managerId }],
              managerId === null
                ? `«${dialogTree.node.display_name}» больше никому не подчиняется.`
                : `«${dialogTree.node.display_name}» теперь подчиняется «${byName(managerId)}».`,
            );
          }}
        />
      ) : null}
      {dialog?.kind === "reports" && dialogTree !== undefined ? (
        <ReportsDialog
          tree={dialogTree}
          hierarchy={hierarchy}
          pending={relocate.isPending}
          onClose={() => setDialog(null)}
          onSubmit={(ids) => {
            setDialog(null);
            if (ids.length === 0) return;
            run(
              ids.map((userId) => ({ userId, managerId: dialogTree.node.id })),
              ids.length === 1
                ? `«${byName(ids[0]!)}» теперь подчиняется «${dialogTree.node.display_name}».`
                : `${ids.length} ${pluralize(ids.length, ["сотрудник", "сотрудника", "сотрудников"])} теперь подчиняются «${dialogTree.node.display_name}».`,
            );
          }}
        />
      ) : null}
    </section>
  );
}

function HierarchyBranch({
  tree,
  ui,
  depth,
}: {
  tree: TreeNode;
  ui: BranchUi;
  depth: number;
}) {
  const open = ui.visible !== null ? true : !ui.collapsed.has(tree.node.id);
  const children =
    ui.visible === null
      ? tree.children
      : tree.children.filter((child) => ui.visible?.has(child.node.id));

  return (
    <li className="hierarchy-branch" data-deep={depth >= DEEP_LEVEL}>
      <PersonCard tree={tree} ui={ui} expandable open={open} depth={depth} />
      {open && children.length > 0 ? (
        <ul className="hierarchy-children">
          {children.map((child) => (
            <HierarchyBranch
              key={child.node.id}
              tree={child}
              ui={ui}
              depth={depth + 1}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function PersonCard({
  tree,
  ui,
  expandable,
  open = true,
  depth = 0,
}: {
  tree: TreeNode;
  ui: BranchUi;
  expandable: boolean;
  open?: boolean;
  depth?: number;
}) {
  const { node } = tree;
  const hasChildren = tree.children.length > 0;
  const blocked = ui.dragId !== null && ui.dragBlocked.has(node.id);
  const droppable = ui.canManage && ui.dragId !== null && !blocked;
  const classes = [
    "hierarchy-card",
    depth === 0 && hasChildren ? "hierarchy-card--top" : "",
    ui.dragId === node.id ? "hierarchy-card--dragging" : "",
    droppable && ui.overId === node.id ? "hierarchy-card--over" : "",
    blocked && ui.dragId !== node.id ? "hierarchy-card--blocked" : "",
    ui.matched?.has(node.id) ? "hierarchy-card--match" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const status = statusLabels[node.status];

  return (
    <div
      className={classes}
      draggable={ui.canManage}
      onDragStart={(event: DragEvent) => {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", node.id);
        ui.startDrag(node.id);
      }}
      onDragEnd={ui.endDrag}
      onDragOver={(event) => {
        if (!droppable) return;
        event.preventDefault();
        ui.setOver(node.id);
      }}
      onDragLeave={() => {
        if (ui.overId === node.id) ui.setOver(null);
      }}
      onDrop={(event) => {
        if (!droppable) return;
        event.preventDefault();
        ui.drop(node.id);
      }}
    >
      {expandable && hasChildren ? (
        <button
          className="hierarchy-card__toggle"
          type="button"
          aria-expanded={open}
          aria-label={`${open ? "Свернуть" : "Развернуть"} подчинённых: ${node.display_name}`}
          disabled={ui.visible !== null}
          onClick={() => ui.toggle(node.id)}
        >
          {open ? (
            <FiChevronDown aria-hidden="true" />
          ) : (
            <FiChevronRight aria-hidden="true" />
          )}
        </button>
      ) : expandable ? (
        <span className="hierarchy-card__toggle hierarchy-card__toggle--empty" />
      ) : null}
      <span className="person-avatar" aria-hidden="true">
        {initials(node.display_name)}
      </span>
      <div className="hierarchy-card__main">
        <strong className="hierarchy-card__name">{node.display_name}</strong>
        <small className="hierarchy-card__role">
          {node.position ?? node.email}
        </small>
        {status || tree.descendants > 0 || depth >= DEEP_LEVEL ? (
          <div className="hierarchy-card__chips">
            {depth >= DEEP_LEVEL ? (
              <span className="hierarchy-card__chip hierarchy-card__level">
                Уровень {depth + 1}
              </span>
            ) : null}
            {status ? (
              <span className={`status-badge status-badge--${node.status}`}>
                {status}
              </span>
            ) : null}
            {tree.descendants > 0 ? (
              <span
                className="hierarchy-card__chip"
                title="Всего в подчинении, включая нижестоящих"
              >
                {tree.descendants} в подчинении
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      {ui.canManage ? (
        <div className="hierarchy-card__actions">
          <button
            className="hierarchy-action"
            type="button"
            title="Добавить подчинённых"
            aria-label={`Добавить подчинённых: ${node.display_name}`}
            onClick={() => ui.openDialog({ kind: "reports", id: node.id })}
          >
            <FiUserPlus aria-hidden="true" />
            <span className="hierarchy-action__label" aria-hidden="true">
              Подчинённые
            </span>
          </button>
          <button
            className="hierarchy-action"
            type="button"
            title="Сменить руководителя"
            aria-label={`Сменить руководителя: ${node.display_name}`}
            onClick={() => ui.openDialog({ kind: "manager", id: node.id })}
          >
            <FiRepeat aria-hidden="true" />
            <span className="hierarchy-action__label" aria-hidden="true">
              Руководитель
            </span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

function ManagerDialog({
  tree,
  hierarchy,
  pending,
  onClose,
  onSubmit,
}: {
  tree: TreeNode;
  hierarchy: Hierarchy;
  pending: boolean;
  onClose: () => void;
  onSubmit: (managerId: string | null) => void;
}) {
  const [managerId, setManagerId] = useState(tree.node.manager_id ?? "");
  // Под собственного подчинённого ставить нельзя: получился бы цикл.
  const blocked = selfAndDescendantIds(tree);
  const options = [...hierarchy.byId.values()]
    .map((candidate) => candidate.node)
    .filter((node) => !blocked.has(node.id))
    .sort((a, b) => a.display_name.localeCompare(b.display_name, "ru"));

  return (
    <Modal title="Сменить руководителя" onClose={onClose}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(managerId === "" ? null : managerId);
        }}
      >
        <p>
          Кому непосредственно подчиняется{" "}
          <strong>{tree.node.display_name}</strong>
          {tree.descendants > 0
            ? `. Его подчинённые (${tree.descendants}) переедут вместе с ним`
            : ""}
          .
        </p>
        <label className="field">
          <span>Руководитель</span>
          <Select
            value={managerId}
            onChange={(event) => setManagerId(event.target.value)}
          >
            <option value="">Без руководителя</option>
            {options.map((node) => (
              <option key={node.id} value={node.id}>
                {describe(node)}
              </option>
            ))}
          </Select>
        </label>
        <div className="modal-actions">
          <button
            className="button button--secondary"
            type="button"
            onClick={onClose}
          >
            Отмена
          </button>
          <button className="button button--primary" disabled={pending}>
            Сохранить
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ReportsDialog({
  tree,
  hierarchy,
  pending,
  onClose,
  onSubmit,
}: {
  tree: TreeNode;
  hierarchy: Hierarchy;
  pending: boolean;
  onClose: () => void;
  onSubmit: (ids: string[]) => void;
}) {
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  // Нельзя подчинить своего руководителя, а прямые подчинённые уже на месте.
  const excluded = new Set([
    tree.node.id,
    ...ancestorIds(hierarchy, tree.node.id),
    ...tree.children.map((child) => child.node.id),
  ]);
  const unplaced = new Set(hierarchy.unplaced.map((node) => node.id));
  const query = filter.trim().toLocaleLowerCase("ru-RU");
  const candidates = [...hierarchy.byId.values()]
    .map((candidate) => candidate.node)
    .filter((node) => !excluded.has(node.id))
    .filter((node) =>
      `${node.display_name} ${node.position ?? ""}`
        .toLocaleLowerCase("ru-RU")
        .includes(query),
    )
    // Сначала те, кого ещё не разместили: чаще всего добавляют именно их.
    .sort(
      (a, b) =>
        Number(unplaced.has(b.id)) - Number(unplaced.has(a.id)) ||
        a.display_name.localeCompare(b.display_name, "ru"),
    );

  return (
    <Modal title="Добавить подчинённых" onClose={onClose}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit([...selected]);
        }}
      >
        <p>
          Кто будет напрямую подчиняться{" "}
          <strong>{tree.node.display_name}</strong>. Тех, кто уже входит в
          другую ветку, переведём сюда.
        </p>
        <label className="field">
          <span>Поиск</span>
          <input
            value={filter}
            placeholder="Имя или должность"
            onChange={(event) => setFilter(event.target.value)}
          />
        </label>
        <ul className="hierarchy-picker">
          {candidates.length === 0 ? (
            <li className="muted">Подходящих сотрудников нет.</li>
          ) : null}
          {candidates.map((node) => (
            <li key={node.id}>
              <label>
                <input
                  type="checkbox"
                  checked={selected.has(node.id)}
                  onChange={(event) =>
                    setSelected((current) => {
                      const next = new Set(current);
                      if (event.target.checked) next.add(node.id);
                      else next.delete(node.id);
                      return next;
                    })
                  }
                />
                <span className="person-avatar" aria-hidden="true">
                  {initials(node.display_name)}
                </span>
                <span className="hierarchy-picker__name">
                  <strong>{node.display_name}</strong>
                  <small>{node.position ?? node.email}</small>
                </span>
                {unplaced.has(node.id) ? (
                  <span className="status-badge">не размещён</span>
                ) : null}
              </label>
            </li>
          ))}
        </ul>
        <div className="modal-actions">
          <button
            className="button button--secondary"
            type="button"
            onClick={onClose}
          >
            Отмена
          </button>
          <button
            className="button button--primary"
            disabled={pending || selected.size === 0}
          >
            {selected.size === 0 ? "Добавить" : `Добавить (${selected.size})`}
          </button>
        </div>
      </form>
    </Modal>
  );
}
